import React, { useState } from 'react';
import { Dialog, IconButton, Text, TextLink } from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_200 } from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import {
  CompleteVehicleEntry,
  Features,
  FeaturesText,
  Picture,
  urlSeparatorFor,
  useServicesContext,
} from '@oneaudi/vtp-shared';
import ModalContent from './ModalContent';
// @ts-ignore
import fallbackImage from './fallbackImage.svg';
import {
  StyledCard,
  CardText,
  StyledHeadline,
  PictureEl,
  PictureWrapper,
  PlayIconWrapper,
  VideoOverlay,
} from './styles';
import { infoLinkTracking, videoIconTracking } from './tracking';
import { decodeHtmlEntities, getValueByKey } from './utils';

export interface ProductVideo {
  similar: boolean;
  posterUrl: string;
  contentType: string;
  vms: {
    baseUrl: string;
    videoId: string;
    type: string;
    suffix: string;
  };
  key: string;
  url: string;
}
export interface ProductType extends Features {
  videos?: ProductVideo[];
}
export interface Props {
  product: ProductType;
  vehicleRaw: CompleteVehicleEntry;
  showImage?: boolean;
}

const Product: React.FC<Props> = ({ product, vehicleRaw, showImage = true }) => {
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const isStandardEquipment = product.kind === 'serie';
  const [showLayer, setShowLayer] = useState(false);

  const infoLinkClickHandler = () => {
    setShowLayer(true);
    infoLinkTracking(trackingService, isStandardEquipment, productHeadline);
  };

  const playIconClickHandler = () => {
    videoIconTracking(trackingService, isStandardEquipment, productHeadline);
    setShowLayer(true);
  };

  const hasAdditionalTexts = () => {
    if (!productTexts || productTexts.length < 1) {
      return false;
    }
    const remarks = getValueByKey<FeaturesText>(productTexts, 'ak_remarks');
    const description = getValueByKey<FeaturesText>(productTexts, 'ak_text');
    const benefits = getValueByKey<FeaturesText>(productTexts, 'benefits');
    const intro = getValueByKey<FeaturesText>(productTexts, 'intro');

    return (
      remarks?.text.length ||
      description?.text.length ||
      benefits?.text.length ||
      intro?.text.length
    );
  };

  let productTexts: FeaturesText[] | undefined;
  if (product.texts) {
    productTexts = Array.isArray(product.texts) ? product.texts : [product.texts];
  }

  const productImage = getValueByKey<Picture>(product.images, 'small_hd_image') || fallbackImage;
  const productHeadline = decodeHtmlEntities(
    getValueByKey<FeaturesText>(productTexts, 'ak_headline')?.text || product.name
  );
  const hasAdditionalText = hasAdditionalTexts();
  const features = vehicleRaw?.detail?.features;
  const hasPackageContent = features?.find((feature) => feature.memberOfPackage === product.family);
  const hasVideo = (product.videos && product.videos.length >= 1) || false;
  const urlSeparator = productImage ? urlSeparatorFor(productImage.url) : '?';
  const imageUrl = productImage?.url ?? fallbackImage;
  const productInfoText = useI18n({
    id: 'stockcars.equipment.info',
    defaultMessage: '{{equipmentTitle}}',
  });
  const moreInfoText = useI18n({
    id: 'stockcars.equipment.more.info',
    defaultMessage: '',
  });

  const productInfoLabel = productInfoText?.replace('{{equipmentTitle}}', productHeadline);
  return (
    <StyledCard>
      <VideoOverlay>
        {productImage && (
          <PictureWrapper>
            {showImage ? (
              <PictureEl
                data-testid="ProductItemPhoto"
                onKeyDown={() => setShowLayer(true)}
                hasVideo={hasVideo}
              >
                {imageUrl.indexOf('mediaservice') > -1 && (
                  <>
                    <source
                      srcSet={`${imageUrl + urlSeparator}wid=400&mimetype=image/webp 1x, ${
                        imageUrl + urlSeparator
                      }wid=600&mimetype=image/webp 2x`}
                      type="image/webp"
                    />
                  </>
                )}
                <img src={imageUrl} alt={productHeadline} loading="lazy" />
              </PictureEl>
            ) : null}
          </PictureWrapper>
        )}
        {hasVideo && (
          <PlayIconWrapper>
            <IconButton
              icon="play"
              size="sm"
              variant="secondary"
              onClick={playIconClickHandler}
              data-testid="ProductVideo"
            />
          </PlayIconWrapper>
        )}
      </VideoOverlay>
      <CardText>
        <StyledHeadline>
          <Text variant={{ type: 'copy', order: '2', style: 'bold' }} data-testid="ProductItemText">
            {productHeadline}
          </Text>
        </StyledHeadline>
        {(productImage || hasAdditionalText || hasPackageContent) && moreInfoText && (
          <TextLink
            variant="standalone"
            icon="forward"
            iconPosition="trailing"
            size="sm"
            margin={[SYS_SPACE_FIXED_200, 0, 0, 0]}
            onClick={infoLinkClickHandler}
            data-testid="ProductItemInfo"
            aria-label={productInfoLabel}
          >
            {moreInfoText}
          </TextLink>
        )}
      </CardText>
      {showLayer && (
        <Dialog
          hasCloseButton
          open={showLayer}
          onClose={() => setShowLayer(false)}
          onCloseButtonAriaLabel="Close"
          variant="focus-large"
        >
          <ModalContent product={product} vehicleRaw={vehicleRaw} />
        </Dialog>
      )}
    </StyledCard>
  );
};

export default Product;
